import type { AspectRatioOption } from '../types';

export const formatDimensions = (width: number, height: number): string => {
  return `${Math.round(width)}×${Math.round(height)}px`;
};

export const getAspectRatioLabel = (aspectRatio: AspectRatioOption): string => {
  const labels: Record<AspectRatioOption, string> = {
    '1:1': 'Square',
    '16:9': 'Landscape',
    '9:16': 'Portrait',
    original: 'Original',
  };
  return labels[aspectRatio];
};

export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return function (...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

export const constrainCrop = (
  x: number,
  y: number,
  width: number,
  height: number,
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; width: number; height: number } => {
  // Ensure crop doesn't exceed image boundaries
  const constrainedX = Math.max(0, Math.min(x, imageWidth - width));
  const constrainedY = Math.max(0, Math.min(y, imageHeight - height));
  const constrainedWidth = Math.min(width, imageWidth - constrainedX);
  const constrainedHeight = Math.min(height, imageHeight - constrainedY);

  return {
    x: constrainedX,
    y: constrainedY,
    width: constrainedWidth,
    height: constrainedHeight,
  };
};

export const percentCropToPixelCrop = (
  percentCrop: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; width: number; height: number } => {
  return {
    x: Math.round((percentCrop.x / 100) * imageWidth),
    y: Math.round((percentCrop.y / 100) * imageHeight),
    width: Math.round((percentCrop.width / 100) * imageWidth),
    height: Math.round((percentCrop.height / 100) * imageHeight),
  };
};

export const pixelCropToPercentCrop = (
  pixelCrop: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number
): { x: number; y: number; width: number; height: number } => {
  return {
    x: (pixelCrop.x / imageWidth) * 100,
    y: (pixelCrop.y / imageHeight) * 100,
    width: (pixelCrop.width / imageWidth) * 100,
    height: (pixelCrop.height / imageHeight) * 100,
  };
};

// Export CSS loader utility
export { loadCropperStyles } from './cssLoader';
